/**
 * 网络请求工具类
 * 基于 fetch API 封装，支持请求/响应拦截、错误处理等
 */

// 请求配置接口
export interface RequestConfig extends RequestInit {
  baseURL?: string
  timeout?: number
  params?: Record<string, any>
  data?: any
}

// 响应数据接口
export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  success?: boolean
}

// 请求错误类
export class RequestError extends Error {
  code: number
  message: string
  data?: any

  constructor(code: number, message: string, data?: any) {
    super(message)
    this.name = 'RequestError'
    this.code = code
    this.message = message
    this.data = data
  }
}

class HttpClient {
  private baseURL: string
  private timeout: number
  private defaultHeaders: HeadersInit

  constructor(baseURL: string = '', timeout: number = 10000) {
    this.baseURL = baseURL || import.meta.env.VITE_API_BASE_URL || 'https://api.emushow.com'
    this.timeout = timeout
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  /**
   * 设置默认请求头
   */
  setDefaultHeaders(headers: HeadersInit) {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers }
  }

  /**
   * 获取认证 token（从 localStorage 或其他存储）
   */
  private getToken(): string | null {
    return localStorage.getItem('token') || sessionStorage.getItem('token')
  }

  /**
   * 构建完整 URL
   */
  private buildURL(url: string, params?: Record<string, any>): string {
    let fullURL = url.startsWith('http') ? url : `${this.baseURL}${url}`
    
    if (params) {
      const searchParams = new URLSearchParams()
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          searchParams.append(key, String(params[key]))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        fullURL += (fullURL.includes('?') ? '&' : '?') + queryString
      }
    }
    
    return fullURL
  }

  /**
   * 请求超时处理
   */
  private createTimeoutPromise(timeout: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new RequestError(408, '请求超时'))
      }, timeout)
    })
  }

  /**
   * 处理响应
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    // 处理 HTTP 错误状态
    if (!response.ok) {
      const errorText = await response.text().catch(() => '')
      throw new RequestError(
        response.status,
        `HTTP Error: ${response.statusText}`,
        errorText
      )
    }

    // 尝试解析 JSON
    let data: ApiResponse<T>
    const contentType = response.headers.get('content-type')
    
    if (contentType && contentType.includes('application/json')) {
      const rawData = await response.json()
      
      // 适配不同的响应格式
      if ('result' in rawData && 'succeed' in rawData) {
        if (!rawData.succeed) {
          throw new RequestError(
            response.status,
            rawData.message || '请求失败',
            rawData.result
          )
        }
        // 转换为标准格式
        data = {
          code: 200,
          message: rawData.message || 'success',
          data: rawData.result as T,
          success: rawData.succeed,
        }
      }
      // 格式1.5: { result, ... }（没有 succeed/code/data）
      // 例如 getcasedetail 返回：{ result: { ...caseDetail... } }
      else if ('result' in rawData) {
        data = {
          code: 200,
          message: rawData.message || 'success',
          data: rawData.result as T,
          success: response.ok,
        }
      }
      // 格式2: { code, message, data } - 标准格式
      else if ('code' in rawData || 'data' in rawData) {
        data = rawData as ApiResponse<T>
      }
      // 其他格式，直接包装
      else {
        data = {
          code: response.status === 200 ? 200 : response.status,
          message: 'success',
          data: rawData as T,
          success: response.ok,
        }
      }
    } else {
      const text = await response.text()
      // 如果不是 JSON，尝试解析为文本响应
      data = {
        code: response.status === 200 ? 200 : response.status,
        message: 'success',
        data: text as any,
        success: response.ok,
      }
    }

    // 根据业务状态码处理（假设 code !== 200 表示业务错误）
    if (data.code && data.code !== 200 && data.code !== 0) {
      throw new RequestError(data.code, data.message || '请求失败', data.data)
    }

    return data
  }

  /**
   * 核心请求方法
   */
  async request<T = any>(
    url: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      baseURL,
      timeout = this.timeout,
      params,
      data,
      headers = {},
      method = 'GET',
      ...restConfig
    } = config

    // 构建完整 URL
    const fullURL = this.buildURL(url, params)

    // 合并请求头
    const mergedHeaders: HeadersInit = {
      ...this.defaultHeaders,
      ...headers,
    }

    // 添加认证 token
    const token = this.getToken()
    if (token) {
      ;(mergedHeaders as Record<string, string>)['Authorization'] = `Bearer ${token}`
    }

    // 构建请求配置
    const requestConfig: RequestInit = {
      method,
      headers: mergedHeaders,
      ...restConfig,
    }

    // 添加请求体（非 GET 请求）
    if (data && method !== 'GET' && method !== 'HEAD') {
      if (data instanceof FormData) {
        // FormData 不设置 Content-Type，让浏览器自动设置
        delete (mergedHeaders as Record<string, string>)['Content-Type']
        requestConfig.body = data
      } else if (typeof data === 'string') {
        requestConfig.body = data
      } else {
        requestConfig.body = JSON.stringify(data)
      }
    }

    try {
      // 创建超时和请求 Promise
      const fetchPromise = fetch(fullURL, requestConfig)
      const timeoutPromise = this.createTimeoutPromise(timeout)

      // 等待请求完成或超时
      const response = await Promise.race([fetchPromise, timeoutPromise])

      // 处理响应
      return await this.handleResponse<T>(response)
    } catch (error) {
      // 处理错误
      if (error instanceof RequestError) {
        throw error
      }
      
      // 网络错误或其他错误
      throw new RequestError(
        0,
        error instanceof Error ? error.message : '网络请求失败',
        error
      )
    }
  }

  /**
   * GET 请求
   */
  get<T = any>(url: string, config?: Omit<RequestConfig, 'method' | 'data'>): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'GET' })
  }

  /**
   * POST 请求
   */
  post<T = any>(url: string, data?: any, config?: Omit<RequestConfig, 'method' | 'data'>): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'POST', data })
  }

  /**
   * PUT 请求
   */
  put<T = any>(url: string, data?: any, config?: Omit<RequestConfig, 'method' | 'data'>): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'PUT', data })
  }

  /**
   * DELETE 请求
   */
  delete<T = any>(url: string, config?: Omit<RequestConfig, 'method' | 'data'>): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'DELETE' })
  }

  /**
   * PATCH 请求
   */
  patch<T = any>(url: string, data?: any, config?: Omit<RequestConfig, 'method' | 'data'>): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'PATCH', data })
  }

  /**
   * 文件上传
   */
  upload<T = any>(url: string, file: File | FormData, config?: Omit<RequestConfig, 'method' | 'data'>): Promise<ApiResponse<T>> {
    const formData = file instanceof FormData ? file : (() => {
      const fd = new FormData()
      fd.append('file', file)
      return fd
    })()

    return this.request<T>(url, {
      ...config,
      method: 'POST',
      data: formData,
    })
  }
}

// 创建默认实例
const httpClient = new HttpClient()

// 导出实例和类
export default httpClient
export { HttpClient }
